/**
 * Shared API client utilities for portal services.
 *
 * Centralizes auth header management, response handling, and base URL
 * configuration so individual service files stay focused on their domain.
 *
 * Includes automatic 401 retry — on an unauthorized response, the client
 * attempts a token refresh (coordinated across tabs) and retries the
 * request once with the new token.
 *
 * Base URLs come from Vite env vars:
 *   VITE_API_URL        — crm-api server (e.g. https://localhost:3000)
 *   VITE_AUTH_SERVER_URL — login server   (e.g. https://localhost:3002)
 */

import { usePortalSiteStore } from '../stores/portalSiteStore';
import { getValidToken, refreshAccessToken } from './portalTokenService';
import { getPortalTenantHeader } from './tenantResolver';

const PORTAL_API_BASE = `${import.meta.env.VITE_API_URL}/api/portal`;
const AUTH_API_BASE = `${import.meta.env.VITE_AUTH_SERVER_URL}/auth/portal`;

/**
 * Parse a fetch response, throwing a descriptive error on non-OK status.
 * Returns the status code alongside the error so callers can inspect it.
 */
class ApiError extends Error {
	public readonly status: number;

	/**
	 * The parsed error body, kept rather than discarded.
	 *
	 * PD-15185 — a refused payment comes back as a structured decline
	 * alongside the message, and the screen needs those parts to present it
	 * properly. Throwing only the message reduced it to the flat string this
	 * ticket exists to remove, and the storefront silently showed nothing.
	 */
	public readonly data: unknown;

	constructor(message: string, status: number, data?: unknown) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
		this.data = data;
	}
}

async function handleResponse<T>(response: Response): Promise<T> {
	if (!response.ok) {
		const body = await response.json().catch(() => ({}));
		const message =
			typeof body === 'object' && body != null && 'message' in body
				? String((body as Record<string, unknown>).message)
				: `Request failed with status ${response.status}`;
		throw new ApiError(message, response.status, body);
	}
	return response.json() as Promise<T>;
}

/**
 * Headers that identify the current brand/portal_site to the backend:
 *
 *   X-Portal-Tenant   — full `<brand>--<tenant>` (or bare `<tenant>`) subdomain
 *                        label. The backend middleware parses this into
 *                        `{ brandAlias, tenantAlias }`. Always sent because
 *                        the Vite dev proxy rewrites the Origin header so
 *                        the server can't parse it directly.
 *   X-Portal-Site-Id  — the resolved `portal_site.id` from the bootstrap
 *                        response. Required for custom-domain requests
 *                        (where the hostname alone can't identify the
 *                        brand) and a useful consistency check otherwise.
 *                        Skipped until bootstrap has populated the store.
 */
function portalContextHeaders(): Record<string, string> {
	const headers: Record<string, string> = {};

	const tenantHeader = getPortalTenantHeader();
	if (tenantHeader != null) {
		headers['X-Portal-Tenant'] = tenantHeader;
	}

	const site = usePortalSiteStore.getState().site;
	if (site != null) {
		headers['X-Portal-Site-Id'] = String(site.portalSiteId);
	}

	return headers;
}

function authHeaders(token: string | null): Record<string, string> {
	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
		...portalContextHeaders(),
	};
	// Anonymous-browsing requests legitimately have no token. Sending a
	// literal `Bearer ` (no token after the space) would look malformed
	// to the auth guard; omit the header entirely so `@Security.
	// PublicPortalOptional()` routes treat the call as anonymous.
	if (token != null && token.length > 0) {
		headers.Authorization = `Bearer ${token}`;
	}
	return headers;
}

/**
 * Make an authenticated request to the portal API (crm-api).
 * Base path: /api/portal
 *
 * On a 401 response (and if this isn't already a retry), attempts a token
 * refresh and retries the request once with the new token.
 */
export async function portalFetch<T>(
	path: string,
	// Null for anonymous browsing — authHeaders omits the Authorization
	// header entirely in that case.
	token: string | null,
	options?: RequestInit,
	isRetry = false,
): Promise<T> {
	// Proactively refresh if the token is expiring soon, so we don't
	// waste a round trip that will just fail at introspection
	const validToken = (await getValidToken()) ?? token;

	const url = `${PORTAL_API_BASE}${path}`;
	const response = await fetch(url, {
		...options,
		headers: {
			...authHeaders(validToken),
			...(options?.headers as Record<string, string> | undefined),
		},
	});

	// On auth failure (401 or 403 from failed introspection), attempt a
	// single token refresh and retry — but only if we had a token to begin
	// with. Anonymous requests legitimately have no token; trying to
	// refresh in that case is pointless and the resulting 401 is the API
	// telling the visitor "this storefront doesn't allow anonymous on
	// this endpoint", not an expired session.
	if (
		(response.status === 401 || response.status === 403) &&
		!isRetry &&
		validToken != null &&
		validToken.length > 0
	) {
		const newToken = await refreshAccessToken();
		if (newToken != null) {
			return portalFetch<T>(path, newToken, options, true);
		}
		// Refresh failed — fall through to handleResponse which will throw
	}

	return handleResponse<T>(response);
}

/**
 * PD-16475 — authenticated multipart upload to the portal API (one file per
 * call, e.g. an image for a support message). Same proactive-refresh +
 * retry-on-401 behavior as `portalFetch`, but WITHOUT the JSON content type:
 * the browser must set `multipart/form-data` with its own boundary.
 */
export async function portalFetchMultipart<T>(
	path: string,
	token: string,
	formData: FormData,
	isRetry = false,
): Promise<T> {
	const validToken = (await getValidToken()) ?? token;
	const headers = authHeaders(validToken);
	delete headers['Content-Type'];

	const response = await fetch(`${PORTAL_API_BASE}${path}`, {
		method: 'POST',
		headers,
		body: formData,
	});

	if (
		(response.status === 401 || response.status === 403) &&
		!isRetry &&
		validToken.length > 0
	) {
		const newToken = await refreshAccessToken();
		if (newToken != null) {
			return portalFetchMultipart<T>(path, newToken, formData, true);
		}
	}

	return handleResponse<T>(response);
}

/**
 * Authenticated portal fetch that returns the raw response body as a Blob —
 * for file downloads (statement PDFs, the statements zip). Same
 * proactive-refresh + retry-on-401 behavior as `portalFetch`. Pass
 * `options` for POST downloads (PD-14241: the zip request carries a JSON
 * body with the checkbox selection).
 */
export async function portalFetchBlob(
	path: string,
	token: string,
	options?: { method?: string; body?: unknown },
	isRetry = false,
): Promise<Blob> {
	const validToken = (await getValidToken()) ?? token;
	const url = `${PORTAL_API_BASE}${path}`;
	const response = await fetch(url, {
		method: options?.method ?? 'GET',
		// authHeaders always carries Content-Type: application/json, which
		// is exactly what the JSON body (when present) needs.
		headers: authHeaders(validToken),
		body: options?.body != null ? JSON.stringify(options.body) : undefined,
	});

	if (
		(response.status === 401 || response.status === 403) &&
		!isRetry &&
		validToken.length > 0
	) {
		const newToken = await refreshAccessToken();
		if (newToken != null) {
			return portalFetchBlob(path, newToken, options, true);
		}
	}

	if (!response.ok) {
		// Mirror handleResponse: surface the server's error message (e.g. the
		// statement-zip "too many statements" cap) instead of a bare status.
		const body: unknown = await response.json().catch(() => ({}));
		const message =
			typeof body === 'object' && body != null && 'message' in body
				? String((body as Record<string, unknown>).message)
				: `Request failed with status ${response.status}`;
		throw new ApiError(message, response.status, body);
	}
	return response.blob();
}

// Portal chat surface lives at `/api/portal/chat/*` — NOT
// `/api/chat/*`, which is internal-CRM-only. Keeping the public
// portal on a single `/api/portal` URL namespace means ecommerce
// never accidentally calls an internal endpoint and makes it
// trivial to gate the private surface at the edge/WAF if needed.
const PORTAL_CHAT_BASE = `${import.meta.env.VITE_API_URL}/api/portal/chat`;

/**
 * Authenticated portal-side fetch for help-chat endpoints. Same
 * refresh-on-401 behavior as `portalFetch` — we just point at a
 * different subtree (`/api/portal/chat` rather than `/api/portal`).
 */
export async function chatFetch<T>(
	path: string,
	token: string,
	options?: RequestInit,
	isRetry = false,
): Promise<T> {
	const validToken = (await getValidToken()) ?? token;
	const url = `${PORTAL_CHAT_BASE}${path}`;
	const response = await fetch(url, {
		...options,
		headers: {
			...authHeaders(validToken),
			...(options?.headers as Record<string, string> | undefined),
		},
	});
	if ((response.status === 401 || response.status === 403) && !isRetry) {
		const newToken = await refreshAccessToken();
		if (newToken != null) {
			return chatFetch<T>(path, newToken, options, true);
		}
	}
	return handleResponse<T>(response);
}

/**
 * Make a request to the auth/login server.
 * Base path: /auth/portal
 */
export async function authFetch<T>(
	path: string,
	options?: RequestInit,
): Promise<T> {
	const url = `${AUTH_API_BASE}${path}`;
	const response = await fetch(url, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			...(options?.headers as Record<string, string> | undefined),
		},
	});
	return handleResponse<T>(response);
}

/**
 * Build a URLSearchParams string from a params object, omitting null/undefined values.
 */
export function buildQueryString(
	params: Record<string, string | number | undefined | null>,
): string {
	const searchParams = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value != null) {
			searchParams.set(key, String(value));
		}
	}
	const qs = searchParams.toString();
	return qs.length > 0 ? `?${qs}` : '';
}
